import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';

export interface Worker {
  worker_id: number;
  worker_name: string;
  pre_execution_script?: { type: string; script: string }[];
  last_seen?: string;
  version?: string;
  enabled?: boolean;
  maintenance_mode_active?: boolean;
  status?: string;
  status_timestamp?: string;
  status_history?: { status: string; timestamp: string }[];
  node_instance_directory?: string;
  skip_ssl_validation?: boolean;
  accept_self_signed?: boolean;
  reconnect_interval?: number;
  sap_binary_directory?: string;
  sap_dyld_library_path?: string;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface WorkerState {
  items: Worker[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    fetchWorkers: boolean;
    getWorkersPaginated: boolean;
  };
}

const initialState: WorkerState = {
  items: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchWorkers: false,
    getWorkersPaginated: false,
  },
};

export const getWorkersPaginated = createAsyncThunk('workers/getWorkersPaginated', async (params: Record<string, any> = {}) => {
  const res = await api.get('/workers', { params: flattenQuery(params) });
  return res.data;
});

export const fetchWorkers = createAsyncThunk('workers/fetchWorkers', async () => {
  const response = await api.get('/workers');
  return response.data;
});

// Thunk for creating a new worker
export const createWorker = createAsyncThunk(
  'workers/createWorker',
  async ({
    worker_name,
    worker_secret,
    pre_execution_script,
    node_instance_directory,
    skip_ssl_validation,
    accept_self_signed,
    reconnect_interval,
    sap_binary_directory,
    sap_dyld_library_path,
  }: {
    worker_name: string;
    worker_secret: string;
    pre_execution_script?: { type: string; script: string }[];
    node_instance_directory?: string;
    skip_ssl_validation?: boolean;
    accept_self_signed?: boolean;
    reconnect_interval?: number;
    sap_binary_directory?: string;
    sap_dyld_library_path?: string;
  }) => {
    const response = await api.post('/workers', {
      worker_name,
      worker_secret,
      pre_execution_script,
      node_instance_directory,
      skip_ssl_validation,
      accept_self_signed,
      reconnect_interval,
      sap_binary_directory,
      sap_dyld_library_path,
    });
    return response.data;
  }
);

// Thunk for fetching a single worker by ID
export const fetchWorkerById = createAsyncThunk('workers/fetchWorkerById', async (worker_id: number) => {
  const response = await api.get(`/workers/${worker_id}`);
  return response.data;
});

// Thunk for updating an existing worker
export const updateWorker = createAsyncThunk(
  'workers/updateWorker',
  async ({
    worker_id,
    worker_name,
    worker_secret,
    pre_execution_script,
    node_instance_directory,
    skip_ssl_validation,
    accept_self_signed,
    reconnect_interval,
    sap_binary_directory,
    sap_dyld_library_path,
  }: {
    worker_id: number;
    worker_name?: string;
    worker_secret?: string;
    pre_execution_script?: { type: string; script: string }[];
    node_instance_directory?: string;
    skip_ssl_validation?: boolean;
    accept_self_signed?: boolean;
    reconnect_interval?: number;
    sap_binary_directory?: string;
    sap_dyld_library_path?: string;
  }) => {
    const response = await api.put(`/workers/${worker_id}`, {
      worker_name,
      worker_secret,
      pre_execution_script,
      node_instance_directory,
      skip_ssl_validation,
      accept_self_signed,
      reconnect_interval,
      sap_binary_directory,
      sap_dyld_library_path,
    });
    return response.data;
  }
);

// Thunk for deleting a worker
export const deleteWorker = createAsyncThunk('workers/deleteWorker', async (worker_name: string) => {
  await api.delete(`/workers/${worker_name}`);
  return worker_name;
});

// Add new thunks for enabling and disabling workers
export const enableWorker = createAsyncThunk('workers/enableWorker', async (worker_name: string) => {
  const response = await api.put(`/workers/${worker_name}/enable`);
  return response.data;
});

export const disableWorker = createAsyncThunk('workers/disableWorker', async (worker_name: string) => {
  const response = await api.put(`/workers/${worker_name}/disable`);
  return response.data;
});

export const restartWorker = createAsyncThunk('workers/restartWorker', async (worker_name: string, { rejectWithValue }) => {
  try {
    await api.post(`/workers/${worker_name}/restart`);
    return worker_name;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.msg || err?.response?.data?.error || err?.message || 'Failed to restart worker');
  }
});

// Thunk for generating and downloading install script
export const generateInstallScript = createAsyncThunk(
  'workers/generateInstallScript',
  async ({ worker_id, server_url, worker_secret, download_url }: { worker_id: number; server_url: string; worker_secret: string; download_url?: string }) => {
    const response = await api.post(`/workers/${worker_id}/generate_install_script`, { server_url, worker_secret, download_url }, { responseType: 'blob' });

    // Extract filename from Content-Disposition header
    const disposition = response.headers['content-disposition'];
    let filename = 'install.sh';
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }

    // Trigger download
    const blob = new Blob([response.data], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
);

const workersSlice = createSlice({
  name: 'workers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchWorkers = true;
      })
      .addCase(fetchWorkers.fulfilled, (state, action) => {
        state.items = action.payload.workers;
        state.status = 'succeeded';
        state.loading.fetchWorkers = false;
      })
      .addCase(fetchWorkers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch workers';
        state.loading.fetchWorkers = false;
      })

      .addCase(getWorkersPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getWorkersPaginated = true;
      })
      .addCase(getWorkersPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { workers, config, filtersConfig, pagination } = action.payload;

        state.items = workers;
        state.config = config;
        state.filtersConfig = filtersConfig || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loading.getWorkersPaginated = false;
      })
      .addCase(getWorkersPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch workers';
        state.loading.getWorkersPaginated = false;
      })

      // Handle createWorker
      .addCase(createWorker.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(createWorker.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create worker';
      })
      // Handle updateWorker
      .addCase(updateWorker.fulfilled, (state, action) => {
        const index = state.items.findIndex((worker) => worker.worker_id === action.payload.worker_id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateWorker.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update worker';
      })
      // Handle deleteWorker
      .addCase(deleteWorker.fulfilled, (state, action) => {
        state.items = state.items.filter((worker) => worker.worker_name !== action.payload);
      })
      .addCase(deleteWorker.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete worker';
      })
      .addCase(fetchWorkerById.fulfilled, (state, action) => {
        const index = state.items.findIndex((worker) => worker.worker_id === action.payload.worker_id);
        if (index !== -1) {
          state.items[index] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(fetchWorkerById.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch worker';
      })
      // Handle enableWorker
      .addCase(enableWorker.fulfilled, (state, action) => {
        const index = state.items.findIndex((worker) => worker.worker_name === action.payload.worker_name);
        if (index !== -1) {
          state.items[index].enabled = action.payload.enabled;
        }
      })
      .addCase(enableWorker.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to enable worker';
      })
      // Handle disableWorker
      .addCase(disableWorker.fulfilled, (state, action) => {
        const index = state.items.findIndex((worker) => worker.worker_name === action.payload.worker_name);
        if (index !== -1) {
          state.items[index].enabled = action.payload.enabled;
        }
      })
      .addCase(disableWorker.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to disable worker';
      })
      .addCase(restartWorker.pending, (state) => {
        state.error = null;
      })
      .addCase(restartWorker.fulfilled, (state, action) => {
        const index = state.items.findIndex((worker) => worker.worker_name === action.payload);
        if (index !== -1) {
          state.items[index].maintenance_mode_active = true;
        }
      })
      .addCase(restartWorker.rejected, (state, action: any) => {
        state.error = action.payload ?? action.error.message ?? 'Failed to restart worker';
      });
  },
});

export default workersSlice.reducer;
